import { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import {
  OrderByOthersRequest,
  OrderByOthersItem,
  TotalOrderValue,
  getOrderByMe,
  getOrderByOthers
} from "../../services/edit-users/edit-users.service";

interface OrdersPopupProps {
  visible: boolean;
  onHide: () => void;
  startDate: string;
  endDate: string;
  salesExecutiveId?: number;
  type: "byMe" | "byOthers" | "total";
  toastRef?: React.RefObject<Toast>;
}

const statusBodyTemplate = (status: string | null) => {
  if (!status) return "--";

  const statusLower = status.toLowerCase();
  let severity: "success" | "info" | "warning" | "danger" | null = null;

  if (statusLower.includes("order received") || statusLower.includes("will order shortly"))
    severity = "success";
  else if (statusLower.includes("call not received") || statusLower.includes("call me later"))
    severity = "warning";
  else if (statusLower.includes("not required")) severity = "info";
  else if (
    statusLower.includes("don't call this number") ||
    statusLower.includes("dnd") ||
    statusLower.includes("do not disturb")
  )
    severity = "danger";
  else if (
    statusLower.includes("not interested") ||
    statusLower.includes("refused") ||
    statusLower.includes("rejected")
  )
    severity = "warning";
  else if (
    statusLower.includes("call back") ||
    statusLower.includes("follow up") ||
    statusLower.includes("pending")
  )
    severity = "info";

  return severity ? <Tag value={status} severity={severity} /> : <Tag value={status} className="bg-gray-300 text-gray-900 px-2 py-1 text-sm" />;
};

const OrdersPopup: React.FC<OrdersPopupProps> = ({
  visible,
  onHide,
  startDate,
  endDate,
  salesExecutiveId,
  type,
  toastRef,
}) => {
  const [data, setData] = useState<OrderByOthersItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>("customerName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const fetchData = async (
    pageNumber: number = page,
    rows: number = pageSize,
    sortColumn: string = sortField,
    sortDirection: "asc" | "desc" = sortOrder
  ) => {
    if (!startDate || !endDate) return;
    setLoading(true);

    const requestData: OrderByOthersRequest = {
      startDate,
      endDate,
      salesExecutiveId,
      pageNumber,
      pageSize: rows,
      sortColumn,
      sortDirection,
    };

   try {
    let response;

    if (type === "byMe") {
      response = await getOrderByMe(requestData);
    } else if (type === "byOthers") {
      response = await getOrderByOthers(requestData); // <-- updated API call
    } else {
      response = await TotalOrderValue(requestData);
    }

    setData(response.data);
    setTotalRecords(response.totalRecords);
  } catch (err) {
    console.error("Failed to fetch orders", err);
    toastRef?.current?.show({
      severity: "error",
      summary: "Error",
      detail: "Failed to fetch orders",
      life: 3000,
    });
  } finally {
    setLoading(false);
  }
};
  useEffect(() => {
    if (visible) fetchData(1, pageSize, sortField, sortOrder);
  }, [visible, startDate, endDate, salesExecutiveId, type]);

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "--";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatRupees = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(amount));
  };

  const handlePageChange = (e: any) => {
    const newPage = (e.page ?? 0) + 1;
    const newSize = e.rows ?? pageSize;
    setPage(newPage);
    setPageSize(newSize);
    fetchData(newPage, newSize, sortField, sortOrder);
  };

  const handleSortChange = (e: any) => {
    const field = e.sortField || "customerName";
    const order = e.sortOrder === 1 ? "asc" : "desc";
    setSortField(field);
    setSortOrder(order);
    fetchData(page, pageSize, field, order);
  };

  const paginatorTemplate = {
    layout:
      "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink NextPageLink LastPageLink",
    RowsPerPageDropdown: (options: any) => (
      <Dropdown
        value={options.value}
        options={[10, 20, 50, 100].map((v) => ({ label: v.toString(), value: v }))}
        onChange={options.onChange}
        className="w-4rem"
      />
    ),
    CurrentPageReport: (options: any) => (
      <span className="mx-3">
        Showing {options.first} to {options.last} of {totalRecords} entries
      </span>
    ),
  };

  return (
    <Dialog
      header={type === "byMe" ? "Orders By Me" : type === "byOthers" ? "Orders By Others" : "Total Orders"}
      visible={visible}
      onHide={onHide}
      style={{ width: "80vw", maxWidth: "1100px" }}
      modal
      className="orders-popup-dialog"
    >
      <DataTable
        value={data}
        paginator
        rows={pageSize}
        totalRecords={totalRecords}
        lazy
        loading={loading}
        first={(page - 1) * pageSize}
        onPage={handlePageChange}
        onSort={handleSortChange}
        sortField={sortField}
        sortOrder={sortOrder === "asc" ? 1 : -1}
        paginatorTemplate={paginatorTemplate}
        className="p-datatable-sm striped-rows shadow-lg"
        responsiveLayout="scroll"
        emptyMessage="No orders found."
        showGridlines
      >
        <Column field="customerName" header="Customer Name" sortable style={{ minWidth: "150px" }} />
        <Column field="primaryContactNbr" header="Contact Number" sortable style={{ minWidth: "120px" }} />
        <Column field="orderID" header="Order ID" sortable style={{ minWidth: "100px" }} />
        <Column field="orderDate" header="Order Date" body={(row) => formatDateTime(row.orderDate)} sortable style={{ minWidth: "120px" }} />
        <Column field="taxableAmount" header="Taxable Amount" body={(row) => formatRupees(row.taxableAmount)} sortable style={{ textAlign: "right", minWidth: "120px" }} />
        <Column field="orderCreatedBy" header="Order Created By" sortable style={{ minWidth: "130px" }} />
        <Column field="lastCalledDate" header="Last Called Date" body={(row) => formatDateTime(row.lastCalledDate)} sortable style={{ minWidth: "120px" }} />
        <Column field="lastCalledStatus" header="Last Called Status" body={(row) => statusBodyTemplate(row.lastCalledStatus)} sortable style={{ minWidth: "150px" }} />
        <Column field="calledBy" header="Called By" sortable style={{ minWidth: "130px" }} />
      </DataTable>
    </Dialog>
  );
};

export default OrdersPopup;
